import { useState, useCallback, useEffect } from "react";
import fixedLogsConfigService from "../services/fixedLogsConfigService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import { useGeneralMessage } from "../../common/APICommon";
import * as XLSX from 'xlsx';

/**
 * Helpers for Date Handling
 */
const formatDateLocal = (dateObj) => {
  if (!dateObj || isNaN(dateObj.getTime())) return null;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateStrict = (val) => {
  if (val === undefined || val === null || val === "") return null;
  let d, m, y;
  if (typeof val === 'number') {
    const excelDate = XLSX.SSF.parse_date_code(val);
    d = excelDate.d; m = excelDate.m; y = excelDate.y;
  } else {
    const cleanStr = String(val).replace(/[<>]/g, "").trim();
    const parts = cleanStr.split(/[/|-]/);
    if (parts.length === 3) {
      d = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      y = parseInt(parts[2], 10);
      if (y < 100) y += 2000;
    } else return null;
  }
  if (!d || !m || !y || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dateObj = new Date(y, m - 1, d);
  if (!isNaN(dateObj.getTime()) && dateObj.getFullYear() === y && dateObj.getMonth() === m - 1 && dateObj.getDate() === d) {
    return dateObj;
  }
  return null;
};

export const useFixedLogsConfig = (employeeId) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState([]);

  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  // Load existing records from DB
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      // If employeeId is provided, get specific. If null (Bulk Mode), get ALL.
      const res = employeeId
        ? await fixedLogsConfigService.getByEmployee(employeeId)
        : await fixedLogsConfigService.getAll();

      setData(res.data || []);
    } catch (err) {
      console.error("Failed to load existing configs", err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const processExcel = (file, allEmployees, activeVessels) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: 'array', raw: true });
      const rawData = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: '' });

      const validated = rawData.map((row, index) => {
        let errors = [];
        const dateFromObj = parseDateStrict(row.DateFrom);
        const dateToObj = parseDateStrict(row.DateTo);
        const empId = String(row.EmployeeId || "").trim();
        const vCode = String(row.VesselCode || "").trim();

        const empMatch = allEmployees?.find(e => String(e.employeeId) === empId);
        const vesselMatch = activeVessels?.find(v => v.code?.trim().toLowerCase() === vCode.toLowerCase());

        // Standard Format Validations
        if (!dateFromObj || !dateToObj) errors.push("Invalid Date format.");
        if (dateFromObj > dateToObj) errors.push("Date From > Date To.");
        if (!empMatch) errors.push(`Employee ${empId} not found.`);
        if (!vesselMatch) errors.push(`Vessel Code ${vCode} not found.`);

        if (dateFromObj && dateToObj && empMatch && vesselMatch) {

          // --- VALIDATION 1: Internal Conflict (Within the Excel file) ---
          const fileConflict = rawData.some((oRow, oIdx) => {
            if (index === oIdx) return false;
            const oFrom = parseDateStrict(oRow.DateFrom);
            const oTo = parseDateStrict(oRow.DateTo);
            return String(oRow.EmployeeId) === empId &&
              String(oRow.VesselCode).toLowerCase() === vCode.toLowerCase() &&
              oFrom && oTo && (dateFromObj <= oTo && oFrom <= dateToObj);
          });
          if (fileConflict) errors.push("Conflict with another row in this file.");

          // --- VALIDATION 2: Database Conflict (Against existing records) ---
          // 'data' contains the results from fetchConfigs (the DB records)
          const dbConflict = data.find(dbRecord => {
            const dbFrom = new Date(dbRecord.dateFrom);
            const dbTo = new Date(dbRecord.dateTo);

            return dbRecord.employeePersonalInformationId === empMatch.id &&
              dbRecord.vesselId === vesselMatch.id &&
              (dateFromObj <= dbTo && dbFrom <= dateToObj);
          });

          if (dbConflict) {
            errors.push(`Conflict with existing record in DB (${formatDateLocal(new Date(dbConflict.dateFrom))} to ${formatDateLocal(new Date(dbConflict.dateTo))}).`);
          }
        }

        return {
          employeeId: empId,
          fullName: empMatch?.fullName || "Unknown",
          employeePersonalInformationId: empMatch?.id || null,
          dateFrom: dateFromObj ? formatDateLocal(dateFromObj) : "INVALID",
          dateTo: dateToObj ? formatDateLocal(dateToObj) : "INVALID",
          vesselCode: vCode,
          vesselName: vesselMatch?.name || "N/A",
          errors: errors.join(". ")
        };
      });
      setUploadPreview(validated);
    };
    reader.readAsArrayBuffer(file);
  };

  // 3. Bulk Upload Logic
  const uploadBulk = async (onSuccess) => {
    setUploadLoading(true);
    try {
      const payload = uploadPreview.map(item => ({
        employeePersonalInformationId: item.employeePersonalInformationId,
        dateFrom: item.dateFrom,
        dateTo: item.dateTo,
        vesselCode: item.vesselCode
      }));
      await fixedLogsConfigService.bulkCreate(payload);
      showGeneralMessage("Configurations uploaded successfully!", true);
      setUploadPreview([]);
      onSuccess?.();
      await fetchConfigs();
    } catch (err) {
      const serverErrors = err.response?.data?.errors;
      showGeneralMessage(serverErrors ? serverErrors.join('\n') : "Upload failed", false);
    } finally {
      setUploadLoading(false);
    }
  };

  // 4. Standard CRUD Handlers (Restored)
  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);
      if (Array.isArray(auditData)) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData) {
        await SetAuditTrail(auditData);
      }
      showGeneralMessage(res.data?.message || "Success", true);
      await fetchConfigs();
      return { success: true };
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "An error occurred", false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.vesselName?.toLowerCase().includes(lower) ||
      f.id?.toString().includes(lower)
    ));
  };

  return {
    loading,
    data,
    showMessage,
    message,
    isSuccess,
    uploadLoading,
    uploadPreview,
    setUploadPreview,
    processExcel,
    uploadBulk,
    searchData,
    createConfig: (p, audit) => handleAction(fixedLogsConfigService.create, audit, p),
    updateConfig: (id, p, audit) => handleAction(fixedLogsConfigService.update, audit, id, p),
    deleteConfig: (id) => handleAction(fixedLogsConfigService.delete, null, id),
    refresh: fetchConfigs
  };
};